import { type ReactNode, useReducer, useMemo, type Dispatch, useCallback } from "react";
import GlobalContext from "./GlobalContext";
import { _config, _resident, _user } from "@/lib/utils";

export const ActionType = {
	LAYOUT: "LAYOUT",
	THEME: "THEME",
	PAGE_TITLE: "PAGE_TITLE",
	LOGGED_IN_USER: "LOGGED_IN_USER",
	PAGE_LOADING: "PAGE_LOADING",
	RESIDENT: "RESIDENT",
	CONFIG: "CONFIG",
} as const;

export type ActionType = typeof ActionType[keyof typeof ActionType];

type ActionValues = User | Resident | string | null | boolean | void | Config;

// Define the types for the context
export interface User {
	id: number;
	email: string;
	first_name: string;
	last_name: string;
	username: string;
	permissions?: string[];
}

export interface BillingAccount {
	account_number: string;
}

// Define the types for the context
export interface Resident {
	user_id: string | null;
	first_name: string | null;
	last_name: string | null;
	other_name: string | null;
	date_of_birth: string | null;
	email: string | null;
	college_email: string | null;
	mobile_number: string | null;
	alternative_mobile_number: string | null;
	address: string | null;
	admission_starting_date: string | null;
	actual_admission_ending_date: string | null;
	faculty_id: string | null;
	training_center_id: string | null;
	subspecialty_id: string | null;
	nationality: string | null;
	gender: string | null;
	emergency_contact_name: string | null;
	emergency_contact_number: string | null;
	emergency_contact_relationship: string | null;
	resident_number: string | null;
	resident_status: string | null;
	admission_ending_date: string | null;
	faculty: string | null;
	training_center: string | null;
	subspecialty: string | null;
	department: string | null;
	force_password_reset: boolean;
}

export interface Config {
	app: {
		name: string;
		timezone: string;
		version: string;
	},
	reseident: Resident
	global: {
		[x: string]: string;
	}
}

export interface GlobalContextType {
	loggedInUser: User | null;
	pageTitle: string;
	layout: string;
	pageLoading: boolean;
	resident: Resident | null;
	theme: string;
	config: Config | null;
}

export interface iProvider {
	controller: GlobalContextType;
	dispatch: Dispatch<IAction>;
	setLoggedInUser: (value: User | null) => void;
	setPageTitle: (value: string) => void;
	setLayout: (value: string) => void;
	setPageLoading: (value: boolean) => void;
	setResident: (value: Resident | null) => void;
	setTheme: (value: string) => void;
	setConfig: (value: Config | null) => void;
	userHasPermission: (perms: string[]) => boolean;
}

export interface IAction {
	value: ActionValues;
	type: ActionType;
}

// Get Main Context
const GcpsUI = GlobalContext;

// Set custom name for the context which is visible on react dev tools
GcpsUI.displayName = "GcpsUIContext";

// React reducer
function reducer(state: GlobalContextType, action: IAction): GlobalContextType {
	switch (action.type) {
		case ActionType.LAYOUT: {
			return { ...state, layout: action.value as string };
		}
		case ActionType.THEME: {
			return { ...state, theme: action.value as string };
		}
		case ActionType.PAGE_TITLE: {
			return { ...state, pageTitle: action.value as string };
		}
		case ActionType.LOGGED_IN_USER: {
			if (action.value !== null) {
				localStorage.setItem(_user, JSON.stringify(action.value));
			} else {
				localStorage.removeItem(_user);
			}
			return { ...state, loggedInUser: action.value as User | null };
		}
		case ActionType.PAGE_LOADING: {
			return { ...state, pageLoading: action.value as boolean };
		}
		case ActionType.RESIDENT: {
			if (action.value !== null) {
				localStorage.setItem(_resident, JSON.stringify(action.value));
			} else {
				localStorage.removeItem(_resident);
			}
			return { ...state, resident: action.value as Resident | null };
		}
		case ActionType.CONFIG: {
			if (action.value !== null) {
				localStorage.setItem(_config, JSON.stringify(action.value));
			} else {
				localStorage.removeItem(_config);
			}
			return { ...state, config: action.value as Config | null };
		}
		default: {
			throw new Error(`Unhandled action type: ${action.type}`);
		}
	}
}

// Create a provider component
const GlobalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	// Provide the values to the context
	const initialState: GlobalContextType = {
		loggedInUser: (() => {
			const storedUser = localStorage.getItem(_user);
			if (storedUser && storedUser !== "undefined") {
				try {
					return JSON.parse(storedUser);
				} catch (error) {
					console.error("Error parsing user from localStorage: ", error);
					localStorage.removeItem(_user);
					return null;
				}
			} else {
				if (storedUser === "undefined") localStorage.removeItem(_user);
				return null;
			}
		})(),
		resident: (() => {
			const storedCustomer = localStorage.getItem(_resident);
			if (storedCustomer && storedCustomer !== "undefined") {
				try {
					return JSON.parse(storedCustomer);
				} catch (error) {
					console.error("Error parsing resident data from localStorage: ", error);
					localStorage.removeItem(_resident);
					return null;
				}
			} else {
				if (storedCustomer === "undefined") localStorage.removeItem(_resident);
				return null;
			}
		})(),
		pageTitle: "Resident Portal",
		layout: "index",
		pageLoading: true,
		theme: "system",
		config: (() => {
			const storedConfig = localStorage.getItem(_config);
			if (storedConfig && storedConfig !== "undefined") {
				try {
					return JSON.parse(storedConfig);
				} catch (error) {
					console.error("Error parsing config from localStorage: ", error);
					localStorage.removeItem(_config);
					return null;
				}
			} else {
				if (storedConfig === "undefined") localStorage.removeItem(_config);
				return null;
			}
		})(),
	};

	const [controller, dispatch] = useReducer(reducer, initialState);

	const userHasPermission = useCallback(
		(perms: string[] = []) => {
			const permissions = controller.loggedInUser?.permissions ?? [];
			if (!permissions.length) {
				return true;
			}
			return permissions.some((x) => perms.includes(x));
		},
		[controller.loggedInUser]
	);

	const setLoggedInUser = (value: User | null) =>
		dispatch({ type: ActionType.LOGGED_IN_USER, value });
	const setPageTitle = (value: string): void =>
		dispatch({ type: ActionType.PAGE_TITLE, value });
	const setLayout = (value: string) =>
		dispatch({ type: ActionType.LAYOUT, value });
	const setPageLoading = (value: boolean) =>
		dispatch({ type: ActionType.PAGE_LOADING, value });
	const setResident = (value: Resident | null) =>
		dispatch({ type: ActionType.RESIDENT, value });
	const setTheme = (value: string) =>
		dispatch({ type: ActionType.THEME, value });
	const setConfig = (value: Config | null) =>
		dispatch({ type: ActionType.CONFIG, value });
	const value = useMemo(
		() => ({
			controller,
			dispatch,
			setLoggedInUser,
			setPageTitle,
			setLayout,
			setPageLoading,
			setResident,
			setTheme,
			userHasPermission,
			setConfig,
		}),
		[controller, dispatch, userHasPermission]
	);

	return <GcpsUI.Provider value={value}>{children}</GcpsUI.Provider>;
};

export { GlobalProvider, GcpsUI as ErpUI };
